import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  hasPaymentConfig, normalizeWaveLink, buildPaymentMessage, whatsappTo,
} from "@/lib/payments";
import { Waves, Copy, Send, Settings, Smartphone, ExternalLink } from "lucide-react";

export function PaymentDialog({ open, onOpenChange, tailleur, defaultAmount = "", clientName, clientPhone }) {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(defaultAmount ? String(defaultAmount) : "");
  const configured = hasPaymentConfig(tailleur);

  useEffect(() => {
    if (open) setAmount(defaultAmount ? String(defaultAmount) : "");
  }, [open, defaultAmount]);

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copié`); }
    catch { toast.error("Copie impossible"); }
  };

  const payWave = () => {
    if (tailleur?.wave_link) {
      window.open(normalizeWaveLink(tailleur.wave_link), "_blank");
      onOpenChange(false);
    } else if (tailleur?.wave_number) {
      copy(tailleur.wave_number, "Numéro Wave");
      toast.info("Ouvrez Wave et envoyez au numéro copié.");
    }
  };

  const payOrange = () => {
    if (tailleur?.om_number) {
      copy(tailleur.om_number, "Numéro Orange Money");
      toast.info("Ouvrez Orange Money (ou #144#) et envoyez au numéro copié.");
    }
  };

  const shareRequest = () => {
    const msg = buildPaymentMessage(tailleur, amount, clientName);
    window.open(whatsappTo(clientPhone, msg), "_blank");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-head text-xl flex items-center gap-2">
            <Smartphone size={20} className="text-tp-green" /> Encaisser un paiement
          </DialogTitle>
          <DialogDescription className="text-sm text-tp-muted">
            Le client règle directement sur votre Wave ou Orange Money.
          </DialogDescription>
        </DialogHeader>

        {!configured ? (
          <div className="text-center py-4">
            <p className="text-tp-muted text-base mb-4">
              Configurez d'abord votre lien Wave et/ou votre numéro Orange Money dans « Mon compte ».
            </p>
            <Button
              data-testid="goto-payment-settings"
              onClick={() => { onOpenChange(false); navigate("/compte"); }}
              className="h-12 rounded-full bg-tp-green hover:bg-tp-greenDark font-bold px-6"
            >
              <Settings size={18} /> Configurer mes paiements
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            <div>
              <label className="font-semibold text-tp-ink block mb-1.5 text-sm">Montant (FCFA)</label>
              <input
                data-testid="payment-amount"
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="Ex: 2500"
                className="w-full h-12 rounded-xl bg-white border border-gray-300 px-4 text-base outline-none focus:border-tp-green"
              />
            </div>

            {(tailleur.wave_link || tailleur.wave_number) && (
              <button
                data-testid="pay-wave-btn"
                onClick={payWave}
                className="w-full h-[54px] rounded-xl text-white font-bold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                style={{ backgroundColor: "#1DC6F2" }}
              >
                <Waves size={20} /> Payer avec Wave {tailleur.wave_link && <ExternalLink size={16} />}
              </button>
            )}
            {tailleur.wave_number && !tailleur.wave_link && (
              <p className="text-sm text-tp-muted -mt-2">Numéro Wave : <b>{tailleur.wave_number}</b></p>
            )}

            {tailleur.om_number && (
              <>
                <button
                  data-testid="pay-orange-btn"
                  onClick={payOrange}
                  className="w-full h-[54px] rounded-xl text-white font-bold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                  style={{ backgroundColor: "#FF7900" }}
                >
                  <Smartphone size={20} /> Payer avec Orange Money
                </button>
                <p className="text-sm text-tp-muted -mt-2 flex items-center justify-between">
                  <span>Numéro OM : <b>{tailleur.om_number}</b></span>
                  <button data-testid="copy-om-btn" onClick={() => copy(tailleur.om_number, "Numéro Orange Money")} className="text-tp-green flex items-center gap-1 font-semibold">
                    <Copy size={14} /> Copier
                  </button>
                </p>
              </>
            )}

            <div className="border-t border-gray-100 pt-3">
              <Button
                data-testid="share-payment-btn"
                onClick={shareRequest}
                className="w-full h-12 rounded-full bg-tp-whatsapp hover:brightness-105 font-bold"
              >
                <Send size={18} /> Envoyer la demande au client
              </Button>
              <p className="text-xs text-tp-muted text-center mt-2">
                Le client reçoit le lien Wave et votre numéro Orange Money sur WhatsApp.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
